<template>
  <div class="result-panel">
    <div v-if="meta" class="result-header">
      <div class="result-header-inner">
        <div class="result-user">
          <div class="result-avatar">{{ avatarText }}</div>
          <div>
            <div class="result-name">{{ meta.userName || '用户' }}</div>
            <div class="result-desc">
              {{ meta.moduleTitle || '—' }}
              <span v-if="meta.userPhone"> · {{ meta.userPhone }}</span>
              <span v-if="meta.createTime"> · {{ formatTime(meta.createTime) }}</span>
            </div>
          </div>
        </div>
        <el-tag size="mini" :type="meta.category === 'scale' ? 'warning' : 'primary'">
          {{ meta.category === 'scale' ? '表观测评' : '功能测评' }}
        </el-tag>
      </div>
    </div>

    <div v-if="!cards.length" class="result-empty">暂无可展示的指标数据</div>

    <div v-else class="result-cards">
      <div v-for="(card, idx) in cards" :key="idx" class="result-card">
        <div class="card-tag">{{ card.tagLabel }}</div>

        <div v-if="card.type === 'validity'" class="card-body">
          <div class="validity-row">
            <span class="validity-label">测谎题错误数</span>
            <span class="validity-value">{{ card.lieErrors != null ? card.lieErrors : '—' }}</span>
          </div>
          <div v-if="card.validity" class="retest-banner">{{ card.validity }}</div>
        </div>

        <div v-else-if="card.type === 'emotion'" class="card-body emotion-body">
          <div v-for="dim in card.dims" :key="dim.label" class="emotion-col">
            <div
              class="half-donut"
              :style="{
                '--deg': dim.percent + 'deg',
                '--color': dim.color
              }"
            >
              <div class="inner-circle"></div>
              <div class="percent-text">{{ dim.percent }}%</div>
            </div>
            <div class="dim-name">{{ dim.label }}</div>
            <div class="tags-row">
              <span class="score-badge">{{ dim.score }}分</span>
              <span class="risk-badge" :style="{ color: dim.color, backgroundColor: dim.bgColor }">{{ dim.level }}</span>
            </div>
          </div>
        </div>

        <div v-else-if="card.type === 'scale-card'" class="card-body">
          <div v-for="(block, bIdx) in card.blocks" :key="bIdx" class="env-section">
            <div v-if="bIdx > 0" class="divider-horizontal"></div>
            <div v-if="block.main && block.main.parsed.isScale" class="env-main-header">
              <span class="env-title">{{ block.main.label }}</span>
              <span class="score-badge">{{ block.main.parsed.score }}分</span>
              <span
                class="risk-badge"
                :style="{ color: block.main.parsed.color, backgroundColor: block.main.parsed.bgColor }"
              >
                {{ block.main.parsed.level }}
              </span>
            </div>
            <div v-for="sub in block.subs" :key="sub.label" class="env-sub-item">
              <div class="env-sub-header">
                <span class="sub-name">{{ sub.label }}</span>
                <span v-if="sub.parsed.isScale" class="sub-score">({{ sub.parsed.score }}分)</span>
                <span
                  v-if="sub.parsed.isScale"
                  class="risk-badge small"
                  :style="{ color: sub.parsed.color, backgroundColor: sub.parsed.bgColor }"
                >
                  {{ sub.parsed.level }}
                </span>
              </div>
              <div v-if="sub.parsed.isScale" class="progress-track">
                <div
                  class="progress-fill"
                  :style="{ width: sub.parsed.percent + '%', backgroundColor: sub.parsed.color }"
                ></div>
              </div>
              <div v-if="sub.parsed.highRisk" class="high-risk-warn">(高危预警)</div>
              <div v-else-if="!sub.parsed.isScale" class="plain-value">{{ sub.parsed.raw }}</div>
            </div>
          </div>
        </div>

        <div v-else-if="card.type === 'achievement'" class="card-body achievement-body">
          <div v-for="item in card.items" :key="item.label" class="achievement-row">
            <span class="achievement-name">{{ item.label }}</span>
            <span class="score-badge">{{ item.score }}分</span>
            <span class="risk-badge" :style="{ color: item.color, backgroundColor: item.bgColor }">{{ item.level }}</span>
          </div>
        </div>

        <div v-else-if="card.type === 'functional'" class="card-body functional-body">
          <div class="metric-grid">
            <div v-for="m in card.metrics" :key="m.label" class="metric-block">
              <div v-if="m.kind === 'percent' || m.kind === 'ms'" class="metric-block-value">
                <span class="metric-main">{{ m.main }}</span>
                <span class="metric-unit">{{ m.unit }}</span>
              </div>
              <div v-else class="metric-block-value metric-block-value--text">
                <span class="metric-main">{{ m.main || m.raw }}</span>
              </div>
              <div class="metric-label">{{ m.label }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { buildAssessmentResultCards } from '../utils/assessmentResultView';

export default {
  name: 'AssessmentResultPanel',
  props: {
    detail: {
      type: Object,
      default: null
    },
    metrics: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    meta() {
      return this.detail || null;
    },
    cards() {
      return buildAssessmentResultCards(this.metrics);
    },
    avatarText() {
      const name = (this.meta && this.meta.userName) || '用';
      return String(name).trim().charAt(0) || '用';
    }
  },
  methods: {
    formatTime(t) {
      if (!t) return '';
      const d = new Date(t);
      if (Number.isNaN(d.getTime())) return String(t);
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
  }
};
</script>

<style scoped>
.result-panel {
  background: #e2ebf4;
  border-radius: 12px;
  overflow: hidden;
}

.result-header {
  background: linear-gradient(135deg, #e1f1fa 0%, #e3f7ed 100%);
  padding: 20px 20px 28px;
  border-radius: 0 0 48% 48% / 0 0 24px 24px;
}

.result-header-inner {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.result-user {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.result-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid rgba(15, 44, 73, 0.12);
  color: #3a4a5f;
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.result-name {
  font-size: 16px;
  font-weight: 700;
  color: #0f2c49;
}

.result-desc {
  margin-top: 4px;
  font-size: 12px;
  color: rgba(15, 44, 73, 0.55);
  line-height: 1.5;
}

.result-cards {
  padding: 0 16px 16px;
  margin-top: -12px;
  position: relative;
  z-index: 1;
  max-height: 62vh;
  overflow-y: auto;
}

.result-empty {
  padding: 24px 16px;
  text-align: center;
  color: #909399;
  font-size: 13px;
}

.result-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
  margin-bottom: 14px;
  overflow: hidden;
  position: relative;
  padding-top: 36px;
}

.card-tag {
  position: absolute;
  top: 0;
  left: 0;
  min-width: 88px;
  padding: 6px 14px 6px 12px;
  background: linear-gradient(135deg, #0d63ee 0%, #3d8bfd 100%);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  border-radius: 0 0 10px 0;
}

.card-body {
  padding: 8px 16px 16px;
}

.validity-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  font-size: 14px;
  color: #303133;
}

.validity-value {
  font-weight: 700;
  color: #0f2c49;
}

.retest-banner {
  margin-top: 8px;
  background: #fff3d6;
  color: #b88230;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  text-align: center;
}

.emotion-body {
  display: flex;
  gap: 8px;
}

.emotion-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 4px 4px;
}

.half-donut {
  width: 88px;
  height: 44px;
  position: relative;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  margin-bottom: 10px;
}

.half-donut::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 88px;
  height: 88px;
  border-radius: 50%;
  background: conic-gradient(
    from 270deg,
    var(--color) 0deg,
    var(--color) var(--deg),
    #e8edf4 var(--deg),
    #e8edf4 180deg,
    transparent 180deg
  );
}

.inner-circle {
  position: absolute;
  top: 12px;
  left: 12px;
  width: 64px;
  height: 64px;
  background: #fff;
  border-radius: 50%;
  z-index: 1;
}

.percent-text {
  position: relative;
  z-index: 2;
  font-size: 13px;
  font-weight: 700;
  color: #303133;
  margin-bottom: 2px;
}

.dim-name {
  font-size: 15px;
  font-weight: 700;
  color: #0f2c49;
  margin-bottom: 8px;
}

.tags-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
}

.score-badge {
  background: #f3f4f6;
  color: #606266;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 6px;
  font-weight: 500;
}

.risk-badge {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 6px;
  font-weight: 700;
}

.risk-badge.small {
  font-size: 10px;
  padding: 1px 6px;
}

.env-section {
  padding-top: 4px;
}

.divider-horizontal {
  height: 1px;
  background: #e8edf4;
  margin: 14px 0;
}

.env-main-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.env-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f2c49;
  margin-right: 4px;
}

.env-sub-item {
  margin-bottom: 12px;
}

.env-sub-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 6px;
}

.sub-name {
  font-size: 13px;
  color: #0f2c49;
  font-weight: 500;
}

.sub-score {
  font-size: 12px;
  color: #909399;
}

.progress-track {
  height: 8px;
  background: #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 8px;
  transition: width 0.2s;
}

.high-risk-warn {
  margin-top: 4px;
  font-size: 11px;
  color: #ea5455;
}

.plain-value {
  font-size: 13px;
  color: #606266;
}

.achievement-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f2f5;
}

.achievement-row:last-child {
  border-bottom: none;
}

.achievement-name {
  flex: 1;
  font-size: 14px;
  color: #0f2c49;
  font-weight: 600;
}

.functional-body {
  padding-top: 4px;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.metric-block {
  background: #f4f6fb;
  border-radius: 10px;
  padding: 12px 10px;
  text-align: center;
}

.metric-block-value {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 2px;
  min-height: 32px;
}

.metric-block-value--text .metric-main {
  font-size: 15px;
  line-height: 1.3;
}

.metric-main {
  font-size: 22px;
  font-weight: 700;
  color: #2a74d7;
  line-height: 1.1;
}

.metric-unit {
  font-size: 12px;
  color: #7b8798;
}

.metric-label {
  margin-top: 6px;
  font-size: 12px;
  color: #7b8798;
}
</style>
